import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  Briefcase, 
  Heart, 
  Music, 
  Users, 
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ChevronRight,
  Users2,
  Building2,
  Handshake,
  ArrowUp,
  Send,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  BookOpen,
  DollarSign,
  Shield,
  Trophy,
  Star,
  Globe,
  Calendar,
  ChevronDown,
  Youtube,
  YoutubeIcon
} from 'lucide-react';
import Hero from "@/components/Hero";
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/components/LanguageContext';
import { toast } from 'react-hot-toast';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const carouselVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0
  })
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

interface ProgramCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
}

interface NewsItem {
  title: string;
  excerpt: string;
  date: string;
  image: string;
  link: string;
}

interface Opportunity {
  title: string;
  type: string;
  location: string;
  link: string;
}

interface Blog {
  id: number;
  title: string;
  content: string;
  image_url: string;
  created_at: string;
  author: string;
  language: 'en' | 'fr';
}

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [[page, direction], setPage] = useState([0, 0]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { language } = useLanguage();
  const [latestBlogs, setLatestBlogs] = useState<Blog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const carouselItems = [
    {
      image: 'https://visionjeunessenouvelle.org.rw/wp-content/uploads/2024/03/education-program.jpg',
      title: t('home.programs.education.title'),
      description: t('home.programs.education.description'),
      icon: <GraduationCap className="h-8 w-8 text-white" />
    },
    {
      image: 'https://visionjeunessenouvelle.org.rw/wp-content/uploads/2024/03/economic-empowerment.jpg',
      title: t('home.programs.economic.title'),
      description: t('home.programs.economic.description'),
      icon: <Briefcase className="h-8 w-8 text-white" />
    },
    {
      image: 'https://visionjeunessenouvelle.org.rw/wp-content/uploads/2024/03/health-program.jpg',
      title: t('home.programs.health.title'),
      description: t('home.programs.health.description'),
      icon: <Heart className="h-8 w-8 text-white" />
    },
    {
      image: 'https://visionjeunessenouvelle.org.rw/wp-content/uploads/2024/03/peace-building.jpg',
      title: t('home.programs.peace.title'),
      description: t('home.programs.peace.description'),
      icon: <Users className="h-8 w-8 text-white" />
    },
    {
      image: 'https://visionjeunessenouvelle.org.rw/wp-content/uploads/2024/03/culture-arts.jpg',
      title: t('home.programs.culture.title'),
      description: t('home.programs.culture.description'),
      icon: <Music className="h-8 w-8 text-white" />
    }
  ];

  const paginate = (newDirection: number) => {
    setPage([(page + newDirection + carouselItems.length) % carouselItems.length, newDirection]);
  };

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);
    return () => clearInterval(timer);
  }, [page]);

  // Handle scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle newsletter signup
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitSuccess(true);
    setEmail('');
    setIsSubmitting(false);
    setTimeout(() => setSubmitSuccess(false), 3000);
  };

  // Scroll to top handler
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const programs = [
    {
      icon: <GraduationCap className="h-8 w-8 text-vjn-blue" />,
      title: t('home.programs.education.title'),
      description: t('home.programs.education.description'),
      link: '/programs/education'
    },
    {
      icon: <DollarSign className="h-8 w-8 text-vjn-blue" />,
      title: t('home.programs.economic.title'),
      description: t('home.programs.economic.description'),
      link: '/programs/economic'
    },
    {
      icon: <Heart className="h-8 w-8 text-vjn-blue" />,
      title: t('home.programs.health.title'),
      description: t('home.programs.health.description'),
      link: '/programs/health'
    },
    {
      icon: <Shield className="h-8 w-8 text-vjn-blue" />,
      title: t('home.programs.peace.title'),
      description: t('home.programs.peace.description'),
      link: '/programs/peace'
    },
    {
      icon: <Trophy className="h-8 w-8 text-vjn-blue" />,
      title: t('home.programs.culture.title'),
      description: t('home.programs.culture.description'),
      link: '/programs/culture'
    }
  ];

  const news = [
    {
      title: t('home.news.item1.title'),
      excerpt: t('home.news.item1.excerpt'),
      date: t('home.news.item1.date'),
      image: "/images/peace-camp.jpg",
      link: '/news/1'
    },
    {
      title: t('home.news.item2.title'),
      excerpt: t('home.news.item2.excerpt'),
      date: t('home.news.item2.date'),
      image: "/images/vocational-center.jpg",
      link: '/news/2'
    },
    {
      title: t('home.news.item3.title'),
      excerpt: t('home.news.item3.excerpt'),
      date: t('home.news.item3.date'),
      image: "/images/health-dance.jpg",
      link: '/news/3'
    }
  ];

  const beneficiaries = [
    t('home.impact.beneficiaries.description'),
    t('home.impact.communities.description'),
    t('home.impact.partners.description'),
    t('home.impact.success.description')
  ];

  const partners = [
    {
      name: "MISEREOR",
      logo: "/images/partners/MISEREOR.png",
      type: "International Development Partner"
    },
    {
      name: "GIZ",
      logo: "/images/partners/giz.jpg",
      type: "German Development Agency"
    },
    {
      name: "Interpeace",
      logo: "/images/partners/interpeace.jpg",
      type: "Peacebuilding Partner"
    },
    {
      name: "European Union",
      logo: "/images/partners/eu.jpg",
      type: "Major Development Partner"
    },
    {
      name: "FHI",
      logo: "/images/partners/fhi.jpg",
      type: "Health Partner"
    },
    {
      name: "EDC",
      logo: "/images/partners/EDC.jpg",
      type: "Education Partner"
    },
    {
      name: "UNHCR",
      logo: "/images/partners/UNHCR.jpg",
      type: "UN Agency"
    },
    {
      name: "FERWAGY",
      logo: "/images/partners/FERWAGY.jpg",
      type: "Sports Federation"
    },
    {
      name: "Terre Sans Frontière",
      logo: "/images/partners/TERRE SANS FRONTIERE.jpg",
      type: "International Partner"
    },
    {
      name: "VTC",
      logo: "/images/partners/vtc.jpg",
      type: "Technical Education"
    },
    {
      name: "MINIYOUTH",
      logo: "/images/partners/MINIYOUTH.jpg",
      type: "Government Partner"
    },
    {
      name: "GOPA",
      logo: "/images/partners/GOPA.jpg",
      type: "Development Partner"
    },
    {
      name: "Gold Youth",
      logo: "/images/partners/GOLD YOUTH.jpg",
      type: "Youth Development"
    },
    {
      name: "La Mennais",
      logo: "/images/partners/LA MENNAIS.jpg",
      type: "Education Partner"
    },
    {
      name: "FERWABA",
      logo: "/images/partners/FERWABA.jpg",
      type: "Sports Federation"
    },
    {
      name: "Rwanda TVET Board",
      logo: "/images/partners/RWANDA TVET BOARD.jpg",
      type: "Technical Education"
    },
    {
      name: "Catholic Relief Services",
      logo: "/images/partners/CATHOLIC RELIEF SERVICES.jpg",
      type: "Humanitarian Partner"
    },
    {
      name: "Ministry of Sports",
      logo: "/images/partners/MINISTRY OF SPORTS.jpg",
      type: "Government Partner"
    },
    {
      name: "Nyundo Diocese",
      logo: "/images/partners/NYUNDO DIOCESE.jpg",
      type: "Religious Partner"
    },
    {
      name: "FERWAHAND",
      logo: "/images/partners/ferwahand.jpg",
      type: "Sports Federation"
    },
    {
      name: "UNDP",
      logo: "/images/partners/UNPD.jpg",
      type: "UN Agency"
    },
    {
      name: "Rwanda Karate",
      logo: "/images/partners/RWANDA KARATE.jpg",
      type: "Sports Federation"
    },
    {
      name: "Rwanda Book Mobile",
      logo: "/images/partners/rwanda book mobile.jpg",
      type: "Education Partner"
    },
    {
      name: "CARE",
      logo: "/images/partners/care.jpg",
      type: "Humanitarian Partner"
    },
    {
      name: "Africa New Life",
      logo: "/images/partners/AFRICA NEW LIFE.jpg",
      type: "Development Partner"
    },
    {
      name: "UNICEF",
      logo: "/images/partners/UNICEF.jpg",
      type: "UN Agency"
    },
    {
      name: "USAID",
      logo: "/images/partners/USAID.jpg",
      type: "Development Partner"
    },
    {
      name: "Right to Play",
      logo: "/images/partners/RIGHT TO PLAY.jpg",
      type: "Sports Development"
    },
    {
      name: "NESA",
      logo: "/images/partners/NESA.jpg",
      type: "Education Partner"
    },
    {
      name: "RTB",
      logo: "/images/partners/rtb.jpg",
      type: "Media Partner"
    },
    {
      name: "Kirehe District",
      logo: "/images/partners/kirehe district.jpg",
      type: "Local Government"
    },
    {
      name: "Burera District",
      logo: "/images/partners/burera district.jpg",
      type: "Local Government"
    },
    {
      name: "CARITAS Rwanda",
      logo: "/images/partners/caritas.jpg",
      type: "Development Partner"
    },
    {
      name: "Gold Youth",
      logo: "/images/partners/gold-youth.jpg",
      type: "Youth Development"
    },
    {
      name: "Athletism",
      logo: "/images/partners/athletism.jpg",
      type: "Sports Development"
    },
    {
      name: "CRS",
      logo: "/images/partners/crs.jpg",
      type: "Humanitarian Partner"
    },
    {
      name: "PFR",
      logo: "/images/partners/pfr.png",
      type: "Education Partner"
    },
    {
      name: "Imbuto Foundation",
      logo: "/images/partners/imbuto.jpg",
      type: "Youth Development"
    },
    {
      name: "Government of Rwanda",
      logo: "/images/partners/govrw.jpg",
      type: "Government Partner"
    },
    {
      name: "FERWAFA",
      logo: "/images/partners/ferwafa.jpg",
      type: "Sports Federation"
    },
    {
      name: "FIC",
      logo: "/images/partners/fic.jpg",
      type: "Local Partner"
    },
    {
      name: "Nyundo",
      logo: "/images/partners/nyundo.jpg",
      type: "Local Partner"
    }
  ];

  const donors = [
    {
      name: "European Union",
      logo: "/images/donors/eu.png",
      type: "Major Donor"
    },
    {
      name: "USAID",
      logo: "/images/donors/usaid.png",
      type: "Development Partner"
    },
    {
      name: "Global Fund",
      logo: "/images/donors/globalfund.png",
      type: "Health Programs"
    },
    {
      name: "Rwanda Development Board",
      logo: "/images/donors/rdb.png",
      type: "Local Partner"
    }
  ];

  useEffect(() => {
    fetchLatestBlogs();
  }, [language]);

  const fetchLatestBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('language', language)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) {
        console.error('Error fetching latest blogs:', error);
        toast.error('Failed to fetch latest blogs');
        return;
      }
      
      setLatestBlogs(data || []);
    } catch (error) {
      console.error('Error fetching latest blogs:', error);
      toast.error('Failed to fetch latest blogs');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Hero />

      {/* Hero Section Enhancement */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video/Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-vjn-blue/90 to-vjn-light-blue/90 z-10" />
          <img
            src="/images/home-slider/youth.JPG"
            alt="VJN Youth Programs"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 relative z-20">
          <motion.div 
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
             Strengthening Youth for a better future
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed">
           Vision Jeunesse Nouvelle is creating opportunities for young people to thrive and make a difference in their communities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="/programs"
                className="bg-white text-vjn-blue px-8 py-4 rounded-full font-semibold hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('hero.explorePrograms', 'Explore Programs')}
                <ChevronRight className="h-5 w-5 ml-2 inline-block" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="/donate"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
              >
                {t('hero.getInvolved', 'Get Involved')}
              </motion.a>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="text-white/80"
          >
            <ChevronDown className="h-8 w-8" />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Who We Are Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-green-600">{t('home.whoWeAre.title')}</h2>
          <p className="text-xl text-center text-gray-600 mb-12 whitespace-pre-line">{t('home.whoWeAre.description')}</p>

          <div className="max-w-5xl mx-auto flex flex-col gap-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-vjn-blue rounded-2xl shadow-lg p-8 flex flex-col items-center text-center">
                <h3 className="text-2xl font-semibold text-white mb-2">{t('home.whoWeAre.missionTitle')}</h3>
                <p className="text-lg text-white whitespace-pre-line">{t('home.whoWeAre.mission')}</p>
              </div>
              <div className="bg-vjn-blue rounded-2xl shadow-lg p-8 flex flex-col items-center text-center">
                <h3 className="text-2xl font-semibold text-white mb-2">{t('home.whoWeAre.visionTitle')}</h3>
                <p className="text-lg text-white whitespace-pre-line">{t('home.whoWeAre.vision')}</p>
              </div>
            </div>
            <div className="bg-vjn-blue rounded-2xl shadow-lg p-8 flex flex-col items-center text-left">
              <h3 className="text-2xl font-semibold text-white mb-2 text-center">{t('home.whoWeAre.differentTitle')}</h3>
              <ul className="list-disc list-inside text-lg text-white space-y-2">
                {t('home.whoWeAre.different').split(/\n\n|\./).map((item, idx) => {
                  const trimmed = item.trim();
                  if (!trimmed) return null;
                  return <li key={idx}>{trimmed}</li>;
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Programs Section Enhancement */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-center mb-16 text-green-600"
          >
            Our Core Programs
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                className="group bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex items-center mb-6">
                  <div className="p-4 rounded-xl bg-vjn-blue/10 group-hover:bg-vjn-blue/20 transition-all duration-300">
                    {program.icon}
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{program.title}</h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">{program.description}</p>
                <motion.a
                  whileHover={{ x: 5 }}
                  href={program.link}
                  className="inline-flex items-center text-vjn-blue font-semibold hover:text-vjn-light-blue transition-colors"
                >
                  Learn More
                  <ChevronRight className="h-5 w-5 ml-1" />
                </motion.a>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Latest News Section Enhancement */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Latest News & Updates
          </h2>
          
          {isLoading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vjn-blue"></div>
            </div>
          ) : latestBlogs.length === 0 ? (
            <p className="text-center text-gray-500">
              No news available at the moment.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestBlogs.map((blog) => (
                <motion.div
                  key={blog.id}
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500"
                >
                  {blog.image_url && (
                    <div className="relative w-full aspect-[16/9] overflow-hidden">
                      <img
                        src={blog.image_url}
                        alt={blog.title}
                        className="w-full h-full object-contain bg-gray-50"
                      />
                    </div>
                  )}
                  <div className="p-8">
                    <h3 className="text-2xl font-semibold mb-4">{blog.title}</h3>
                    <div 
                      className="text-gray-600 mb-6 line-clamp-3 leading-relaxed prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: blog.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' 
                      }}
                    />
                    <div className="flex justify-between items-center text-sm text-gray-500 mb-6">
                      <span className="flex items-center">
                        <Users2 className="h-4 w-4 mr-2" />
                        {blog.author}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        {new Date(blog.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <motion.a
                      whileHover={{ x: 5 }}
                      href={`/blog/${blog.id}`}
                      className="inline-flex items-center text-vjn-blue font-semibold hover:text-vjn-light-blue transition-colors"
                    >
                      Read More
                      <ChevronRight className="h-5 w-5 ml-1" />
                    </motion.a>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <motion.div 
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="/news"
              className="inline-flex items-center bg-vjn-blue text-white px-8 py-4 rounded-full font-semibold hover:bg-vjn-light-blue transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              View All News
              <ChevronRight className="h-5 w-5 ml-2" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-24 bg-gradient-to-br from-vjn-blue to-vjn-light-blue relative overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-center text-white mb-8"
          >
            {t('home.beneficiaries.title')}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-xl text-center text-white/90 mb-16 max-w-4xl mx-auto"
          >
            {t('home.beneficiaries.description')}
          </motion.p>
          <motion.div 
            variants={fadeInUp}
            className="max-w-6xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(t('home.beneficiaries.list', { returnObjects: true }) as any[]).map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex flex-col">
                    <div className="flex items-center mb-4">
                      <span className="text-3xl mr-3">{item.icon}</span>
                      <h3 className="text-white text-xl font-semibold">{item.title}</h3>
                    </div>
                    <p className="text-white/90">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Get in Touch Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-green-600">{t('home.getInTouch.title')}</h2>
          <p className="text-xl text-center text-gray-600 mb-8">{t('home.getInTouch.subtitle')}</p>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">{t('home.getInTouch.description')}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            <div>
              <h3 className="text-xl font-semibold mb-4">{t('home.getInTouch.contactInfo.title')}</h3>
              <p className="mb-2">{t('home.getInTouch.contactInfo.address')}</p>
              <p className="mb-2">{t('home.getInTouch.contactInfo.phone')}</p>
              <p className="mb-2">{t('home.getInTouch.contactInfo.email')}</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">{t('home.getInTouch.socialMedia.title')}</h3>
              <div className="flex space-x-4 justify-center mt-8">
                <a href="#" className="bg-white rounded-full p-3 shadow hover:bg-vjn-blue/10 transition-colors">
                  <Facebook className="h-5 w-5 text-vjn-blue" />
                </a>
                <a href="#" className="bg-white rounded-full p-3 shadow hover:bg-vjn-blue/10 transition-colors">
                  <Twitter className="h-5 w-5 text-vjn-blue" />
                </a>
                <a href="#" className="bg-white rounded-full p-3 shadow hover:bg-vjn-blue/10 transition-colors">
                  <Instagram className="h-5 w-5 text-vjn-blue" />
                </a>
                <a href="https://www.linkedin.com/in/visionjeunesse2" target="_blank" rel="noopener noreferrer" className="bg-white rounded-full p-3 shadow hover:bg-vjn-blue/10 transition-colors">
                  <Linkedin className="h-5 w-5 text-vjn-blue" />
                </a>
                <a href="https://www.tiktok.com/@visionjeunesse2" target="_blank" rel="noopener noreferrer" className="bg-white rounded-full p-3 shadow hover:bg-vjn-blue/10 transition-colors flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-vjn-blue" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.5 3v2.25A4.75 4.75 0 0 0 21 10v1.25A7.75 7.75 0 1 1 9.25 3H11A6.75 6.75 0 1 0 17.75 10V3h-1.25z" />
                  </svg>
                </a>
                <a href="https://www.youtube.com/@visionjeunesse2" target="_blank" rel="noopener noreferrer" className="bg-white rounded-full p-3 shadow hover:bg-vjn-blue/10 transition-colors">
                  <YoutubeIcon className="h-5 w-5 text-vjn-blue" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4 mt-8">
            <button className="bg-vjn-blue text-white px-6 py-2 rounded-lg hover:bg-vjn-blue-dark">
              {t('home.getInTouch.buttons.contact')}
            </button>
            <button className="bg-vjn-green text-white px-6 py-2 rounded-lg hover:bg-vjn-green-dark">
              {t('home.getInTouch.buttons.donate')}
            </button>
            <button className="bg-vjn-orange text-white px-6 py-2 rounded-lg hover:bg-vjn-orange-dark">
              {t('home.getInTouch.buttons.volunteer')}
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section Enhancement */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-24 bg-gray-50 relative overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <motion.div 
            variants={fadeInUp}
            className="max-w-6xl mx-auto"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-4xl font-bold mb-8">Get in Touch</h2>
                <div className="space-y-6">
                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="flex items-center bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <MapPin className="h-8 w-8 text-vjn-blue mr-4" />
                    <div>
                      <p className="font-semibold text-lg mb-1">Address</p>
                      <p className="text-gray-600">Rubavu District, Gisenyi Sector, Nengo Cell, Gikarani Village</p>
                    </div>
                  </motion.div>
                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="flex items-center bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Phone className="h-8 w-8 text-vjn-blue mr-4" />
                    <div>
                      <p className="font-semibold text-lg mb-1">Phone</p>
                      <p className="text-gray-600">+250 784 847 218</p>
                      <p className="text-gray-600">+250 783 999 243</p>
                    </div>
                  </motion.div>
                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="flex items-center bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Mail className="h-8 w-8 text-vjn-blue mr-4" />
                    <div>
                      <p className="font-semibold text-lg mb-1">Email</p>
                      <p className="text-gray-600">visionjeunesse2050@gmail.com</p>
                    </div>
                  </motion.div>
                </div>
                <div className="flex space-x-4 mt-8">
                  <motion.a 
                    whileHover={{ y: -5 }}
                    href="https://facebook.com/visionjeunesse2" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Facebook className="h-6 w-6 text-vjn-blue" />
                  </motion.a>
                  <motion.a 
                    whileHover={{ y: -5 }}
                    href="https://twitter.com/visionjeunesse2" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Twitter className="h-6 w-6 text-vjn-blue" />
                  </motion.a>
                  <motion.a 
                    whileHover={{ y: -5 }}
                    href="https://instagram.com/visionjeunesse2" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Instagram className="h-6 w-6 text-vjn-blue" />
                  </motion.a>
                </div>
              </div>
              <div>
                <h2 className="text-4xl font-bold mb-8">Our Location</h2>
                <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5064444799146!2d29.257187315302952!3d-1.7022109986683071!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19dd050a47fb11e7%3A0xe550726afa8eb90a!2sVision%20Jeunesse%20Nouvelle!5e0!3m2!1sen!2srw!4v1647856231584!5m2!1sen!2srw"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Vision Jeunesse Nouvelle Location"
                    className="hover:opacity-95 transition-opacity duration-300"
                  ></iframe>
                </div>
                <div className="mt-6">
                  <div className="bg-white p-4 rounded-lg shadow-md">
                    <p className="text-gray-600 flex items-center justify-center font-medium">
                      <Globe className="h-5 w-5 mr-2 text-vjn-blue" />
                      Located in Gisenyi, Rubavu District, Western Province, Rwanda
                    </p>
                    <p className="text-sm text-gray-500 text-center mt-2">
                      Click the map to view in full screen or get directions
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Scroll to Top Button Enhancement */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-vjn-blue text-white p-4 rounded-full shadow-lg hover:bg-vjn-light-blue transition-all duration-300 z-50"
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowUp className="h-6 w-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home; 